import React, { createContext, useContext, useEffect, useState } from 'react'

const ThemeContext = createContext()

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    // Try to get theme from localStorage
    if (typeof window !== 'undefined') {
      const savedTheme = localStorage.getItem('zentrik-theme')
      if (savedTheme) {
        return savedTheme
      }
      // Check system preference - Cambiado para usar modo claro como predeterminado
      if (window.matchMedia('(prefers-color-scheme: light)').matches) {
        return 'light'
      }
      // Si no hay preferencia específica del sistema, usar modo claro por defecto
      return 'light'
    }
    return 'light'
  })

  const toggleTheme = () => {
    setTheme(prevTheme => prevTheme === 'light' ? 'dark' : 'light')
  }

  const setLightTheme = () => setTheme('light')
  const setDarkTheme = () => setTheme('dark')

  useEffect(() => {
    // Save to localStorage
    localStorage.setItem('zentrik-theme', theme)
    
    // Apply theme to document
    const root = document.documentElement
    
    if (theme === 'dark') {
      root.classList.add('dark')
    } else {
      root.classList.remove('dark')
    }
    
    // Update CSS variables for dark theme
    if (theme === 'dark') {
      // Dark theme variables
      root.style.setProperty('--glass-bg', 'rgba(15, 23, 42, 0.95)')
      root.style.setProperty('--glass-border', 'rgba(148, 163, 184, 0.18)')
      root.style.setProperty('--glass-shadow', '0 8px 32px 0 rgba(0, 0, 0, 0.5)')
      root.style.setProperty('--dashboard-bg', '#0f172a')
      root.style.setProperty('--dashboard-card-bg', 'rgba(30, 41, 59, 0.95)')
      root.style.setProperty('--dashboard-text', '#f8fafc')
      root.style.setProperty('--dashboard-text-secondary', '#cbd5e1')
      root.style.setProperty('--dashboard-border', 'rgba(148, 163, 184, 0.2)')
      root.style.setProperty('--sidebar-bg', '#000234')
      root.style.setProperty('--sidebar-text', '#ffffff')
      root.style.setProperty('--sidebar-hover', 'rgba(255, 255, 255, 0.1)')
      root.style.setProperty('--sidebar-active', '#fe6c3b')
    } else {
      // Light theme variables
      root.style.setProperty('--glass-bg', 'rgba(255, 255, 255, 0.85)')
      root.style.setProperty('--glass-border', 'rgba(255, 255, 255, 0.18)')
      root.style.setProperty('--glass-shadow', '0 8px 32px 0 rgba(31, 38, 135, 0.37)')
      root.style.setProperty('--dashboard-bg', '#f8fafc')
      root.style.setProperty('--dashboard-card-bg', 'rgba(255, 255, 255, 0.95)')
      root.style.setProperty('--dashboard-text', '#1f2937')
      root.style.setProperty('--dashboard-text-secondary', '#6b7280')
      root.style.setProperty('--dashboard-border', 'rgba(209, 213, 219, 0.5)')
      root.style.setProperty('--sidebar-bg', '#000234')
      root.style.setProperty('--sidebar-text', '#ffffff')
      root.style.setProperty('--sidebar-hover', 'rgba(255, 255, 255, 0.1)')
      root.style.setProperty('--sidebar-active', '#fe6c3b')
    }
  }, [theme])

  // Listen for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    
    const handleChange = (e) => {
      // Only auto-switch if user hasn't manually set a preference
      const savedTheme = localStorage.getItem('zentrik-theme')
      if (!savedTheme) {
        // Cambiado para usar modo claro como predeterminado en lugar de seguir la preferencia del sistema
        setTheme('light')
      }
    }

    mediaQuery.addEventListener('change', handleChange)
    return () => mediaQuery.removeEventListener('change', handleChange)
  }, [])

  const value = {
    theme,
    toggleTheme,
    setLightTheme,
    setDarkTheme,
    isDark: theme === 'dark',
    isLight: theme === 'light'
  }

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  )
}

export const useTheme = () => {
  const context = useContext(ThemeContext)
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}

export default useTheme